import React, { useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { ConditionsEditor } from "../Conditions";
import MessageEditor from "./MessageEditor";

/**
 * REMINDERS — the sequences, their reminders, and what the calendar would do
 * with them.
 *
 * The shape follows the config, because the config's shape is the argument (see
 * src/automations/callConfig.js): a list of SEQUENCES chosen by which call it is
 * with that company, each holding a list of REMINDERS at their own offsets
 * before the call. Anything flatter would have hidden the one thing Gabriel
 * asked for by name — different emails for a first call than for a third.
 *
 * SEQUENCE ORDER IS MEANINGFUL and the page has to say so, because the engine
 * takes the first match. A list that looked unordered would make "I edited the
 * second-call copy and nothing changed" an easy afternoon to lose.
 *
 * REMINDER ORDER IS ALSO MEANINGFUL, in the other direction: they are listed
 * furthest from the call first, and the server refuses a config that is not. So
 * the editor sorts on edit rather than letting him save something that will be
 * rejected — changing "1 hour" to "2 days" moves the row, which is surprising
 * for about one second and correct forever after.
 *
 * THE PREVIEW IS THE POINT OF THE TAB. It answers "if I turn this on, who gets
 * mail" against the real calendar, and the interesting rows are the refusals: a
 * call whose company left the board, a third call at a company with two
 * sequences written, a call booked this morning for this afternoon whose
 * day-before reminder has already missed its moment.
 */

const UNITS = [
  { key: "minutes", one: "minute", many: "minutes" },
  { key: "hours", one: "hour", many: "hours" },
  { key: "days", one: "day", many: "days" },
];

const UNIT_MINUTES = { minutes: 1, hours: 60, days: 24 * 60 };

function offsetMinutes(before) {
  if (!before) return 0;
  return Math.round(Number(before.value || 0) * (UNIT_MINUTES[before.unit] || 0));
}

/** "2 days before" / "1 hour before". */
function describeOffset(before) {
  const value = Number(before && before.value) || 0;
  const unit = UNITS.find((u) => u.key === (before && before.unit)) || UNITS[1];
  return `${value} ${value === 1 ? unit.one : unit.many} before`;
}

/*
 * WHICH CALL A SEQUENCE APPLIES TO, in the three forms the schema allows. The
 * open-ended one carries its own anchor ("3+") rather than being a bare
 * "later", because a form you have to read alongside its neighbours to know what
 * it covers is how the first draft of this shipped a config with no sequence for
 * a third call at all.
 */
const CALL_NUMBER_OPTIONS = [
  { value: "1", label: "The first call" },
  { value: "2", label: "The second call" },
  { value: "3", label: "The third call" },
  { value: "4", label: "The fourth call" },
  { value: "2+", label: "The second call and after" },
  { value: "3+", label: "The third call and after" },
  { value: "4+", label: "The fourth call and after" },
  { value: "any", label: "Any call" },
];

function callNumberValue(sequence) {
  const raw = sequence.call_number;
  return raw === undefined || raw === null ? "any" : String(raw);
}

/** The form's string back into what the schema stores: a number, "3+", or "any". */
function parseCallNumber(raw) {
  if (raw === "any") return "any";
  if (raw.endsWith("+")) return raw;
  return Number(raw);
}

const BLANK_REMINDER = {
  label: "",
  before: { value: 1, unit: "hours" },
  invite_response: "any",
  subject: "",
  body: "",
};

/* ------------------------------------------------------------------ *
 * One reminder.
 * ------------------------------------------------------------------ */

function Reminder({ reminder, index, total, variables, threadMode, picked, onPick, onChange, onRemove }) {
  const set = (patch) => onChange({ ...reminder, ...patch });
  const [open, setOpen] = useState(index === 0);

  return (
    <div className="crm-auto-rem">
      <div className="crm-auto-rem__head">
        <button
          type="button"
          className="crm-auto-rem__toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? "▾" : "▸"}
        </button>

        <span className="crm-auto-rem__n crm-num">{index + 1}</span>

        <input
          className="crm-input crm-auto-rem__when"
          type="number"
          min="1"
          max="60"
          value={reminder.before?.value ?? 1}
          onChange={(event) =>
            set({ before: { ...reminder.before, value: Number(event.target.value) || 1 } })
          }
          aria-label="How long before the call"
        />
        <select
          className="crm-select crm-auto-rem__unit"
          value={reminder.before?.unit || "hours"}
          onChange={(event) => set({ before: { ...reminder.before, unit: event.target.value } })}
          aria-label="Unit"
        >
          {UNITS.map((unit) => (
            <option key={unit.key} value={unit.key}>
              {unit.many}
            </option>
          ))}
        </select>
        <span className="crm-auto-rem__before">before the call</span>

        <input
          className="crm-input crm-auto-rem__label"
          value={reminder.label || ""}
          placeholder="Name it (optional)"
          onChange={(event) => set({ label: event.target.value })}
          aria-label="Reminder name"
        />

        <button
          type="button"
          className="crm-rb__x"
          onClick={onRemove}
          aria-label={`Remove reminder ${index + 1}`}
          disabled={total === 1}
          title={total === 1 ? "A sequence needs at least one reminder" : undefined}
        >
          ✕
        </button>
      </div>

      {open && (
        <div className="crm-auto-rem__body">
          {/*
            THE SUBJECT IS ALWAYS EDITABLE, and disabling it in "continue" mode
            was wrong rather than merely annoying: continuing a thread is a
            PREFERENCE, not a guarantee. A lead with no thread on file — a warm
            intro, a first booking, anyone who has only ever been written to by
            hand from another mailbox — has nothing to reply into, and the send
            falls back to starting a new thread with exactly this subject. A
            greyed-out box meant that fallback was whatever had been typed
            before somebody switched the mode.
          */}
          <MessageEditor
            subject={reminder.subject || ""}
            body={reminder.body || ""}
            variables={variables}
            rows={12}
            label={`Reminder ${index + 1}`}
            subjectHint={
              threadMode === "continue"
                ? "Used when there is no thread to reply into. Otherwise the reply takes that thread's subject, prefixed “Re:”."
                : "Every reminder starts a new thread with this subject."
            }
            onChange={(patch) => set(patch)}
          />

          <label className="crm-auto-inline">
            <input
              type="radio"
              name="test-pick"
              checked={!!picked}
              onChange={onPick}
            />
            <span>Send me the test of this one</span>
          </label>

          <label className="crm-auto-inline">
            <span>Send this one</span>
            <select
              className="crm-select"
              value={reminder.invite_response || "any"}
              onChange={(event) => set({ invite_response: event.target.value })}
              aria-label={`Reminder ${index + 1} invite response`}
            >
              <option value="any">whatever they did with the invite</option>
              <option value="unconfirmed">only if they have NOT accepted the invite</option>
              <option value="accepted">only if they HAVE accepted the invite</option>
            </select>
          </label>
          <p className="crm-hint">
            “Whatever they did” by default: a reminder is a courtesy, and a courtesy that only
            arrives when you have been rude is a strange thing to receive. Use “not accepted” to
            chase a silence, and “accepted” for the one that assumes the meeting is real — the hour
            before, carrying the link. An invite with no attendee list reads as not accepted either
            way, because a record that says nothing must not be read as a yes.
          </p>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * One sequence.
 * ------------------------------------------------------------------ */

function Sequence({
  sequence,
  index,
  total,
  fields,
  variables,
  threadMode,
  pick,
  onPick,
  onChange,
  onRemove,
  onMove,
}) {
  const set = (patch) => onChange({ ...sequence, ...patch });

  const setReminder = (i, next) => {
    const reminders = sequence.reminders.map((r, j) => (j === i ? next : r));
    // Furthest from the call first. The server refuses any other order, so
    // sorting here means an edit never produces a config that cannot be saved.
    reminders.sort((a, b) => offsetMinutes(b.before) - offsetMinutes(a.before));
    set({ reminders });
  };

  const hasFilter = sequence.conditions != null;

  return (
    <section className="crm-card crm-auto-seq">
      <header className="crm-auto-seq__head">
        <span className="crm-auto-seq__order crm-num">{index + 1}</span>
        <input
          className="crm-input crm-auto-seq__name"
          value={sequence.name || ""}
          placeholder="Name this sequence"
          onChange={(event) => set({ name: event.target.value })}
          aria-label="Sequence name"
        />
        <select
          className="crm-select"
          value={callNumberValue(sequence)}
          onChange={(event) => set({ call_number: parseCallNumber(event.target.value) })}
          aria-label="Which call this applies to"
        >
          {CALL_NUMBER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <div className="crm-auto-seq__moves">
          <button type="button" onClick={() => onMove(index - 1)} disabled={index === 0} aria-label="Move up">
            ↑
          </button>
          <button
            type="button"
            onClick={() => onMove(index + 1)}
            disabled={index === total - 1}
            aria-label="Move down"
          >
            ↓
          </button>
          <button
            type="button"
            className="crm-rb__x"
            onClick={onRemove}
            disabled={total === 1}
            title={total === 1 ? "Keep at least one sequence" : undefined}
            aria-label="Remove sequence"
          >
            ✕
          </button>
        </div>
      </header>

      {/*
        The optional extra filter. Off by default because the call number is the
        axis Gabriel asked for and most sequences need nothing else; a condition
        builder open on every sequence would bury that.
      */}
      <div className="crm-auto-seq__filter">
        <label className="crm-auto-inline">
          <input
            type="checkbox"
            checked={hasFilter}
            onChange={(event) => set({ conditions: event.target.checked ? { all: [] } : null })}
          />
          <span>Also only when the company matches a filter</span>
        </label>
        {hasFilter && (
          <ConditionsEditor
            conditions={sequence.conditions}
            fields={fields}
            title="Use this sequence when"
            emptyNote="No conditions yet, so this sequence applies to every call of that number."
            onChange={(next) => set({ conditions: next || { all: [] } })}
          />
        )}
      </div>

      <div className="crm-auto-seq__reminders">
        {(sequence.reminders || []).map((reminder, i) => (
          <Reminder
            key={i}
            reminder={reminder}
            index={i}
            total={sequence.reminders.length}
            variables={variables}
            threadMode={threadMode}
            picked={pick && pick.sequenceIndex === index && pick.stepIndex === i}
            onPick={() => onPick({ sequenceIndex: index, stepIndex: i })}
            onChange={(next) => setReminder(i, next)}
            onRemove={() => set({ reminders: sequence.reminders.filter((_, j) => j !== i) })}
          />
        ))}
      </div>

      <button
        type="button"
        className="crm-rb__add"
        onClick={() => {
          const reminders = [...(sequence.reminders || []), BLANK_REMINDER];
          reminders.sort((a, b) => offsetMinutes(b.before) - offsetMinutes(a.before));
          set({ reminders });
        }}
      >
        + add a reminder
      </button>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * The preview.
 * ------------------------------------------------------------------ */

function Preview({ id, config }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const latest = useRef(0);

  useEffect(() => {
    const token = ++latest.current;
    const timer = setTimeout(() => {
      setLoading(true);
      api(`/automations/${id}/calls/preview`, {
        method: "POST",
        body: JSON.stringify({ config }),
      })
        .then((next) => {
          if (token === latest.current) setResult(next);
        })
        .catch((err) => {
          if (token === latest.current) setResult({ errors: [err.message], calls: [] });
        })
        .finally(() => {
          if (token === latest.current) setLoading(false);
        });
    }, 500);
    return () => clearTimeout(timer);
  }, [id, config]);

  const calls = (result && result.calls) || [];
  const errors = (result && result.errors) || [];

  return (
    <aside className="crm-card crm-auto-preview">
      <header className="crm-auto-preview__head">
        <h3>The calls already booked</h3>
        {loading && <span className="crm-hint">checking…</span>}
      </header>

      {!!errors.length && <div className="crm-rb__err">{errors.join(" ")}</div>}

      {!errors.length && result && (
        <p className="crm-hint">
          {result.matched} of {result.considered} upcoming {result.considered === 1 ? "call" : "calls"} would
          get reminders.
        </p>
      )}

      {!calls.length && !loading && !errors.length && (
        <p className="crm-hint">No calls on the calendar inside the planning horizon.</p>
      )}

      <ul className="crm-auto-preview__list">
        {calls.map((call) => (
          <li key={call.event_id} className={call.skipped_because ? "is-skipped" : ""}>
            <div className="crm-auto-preview__who">
              <b>{call.company || call.contact}</b>
              <span className="crm-hint">
                {new Date(call.call_at).toLocaleString(undefined, {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                {" · "}
                call {call.call_number}
              </span>
            </div>

            {call.skipped_because ? (
              <p className="crm-hint crm-hint--warn">{call.skipped_because}</p>
            ) : (
              <>
                <p className="crm-hint">{call.sequence}</p>
                <ul className="crm-auto-preview__rem">
                  {call.reminders.map((reminder) => (
                    <li key={reminder.step_index} className={reminder.in_the_past ? "is-missed" : ""}>
                      {reminder.when}
                      {reminder.in_the_past ? " · too late, skipped" : ""}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </li>
        ))}
      </ul>
    </aside>
  );
}

/* ------------------------------------------------------------------ *
 * The tab.
 * ------------------------------------------------------------------ */

export default function RemindersTab({ id, config, patch, meta, pick, onPick }) {
  const sequences = config.sequences || [];
  const fields = meta?.filter_vocabulary || [];
  const variables = meta?.variables || [];

  const setSequences = (next) => patch("sequences", next);

  const move = (from, to) => {
    if (to < 0 || to >= sequences.length) return;
    const next = [...sequences];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    setSequences(next);
  };

  return (
    <div className="crm-auto-two">
      <div className="crm-auto-two__main">
        <section className="crm-card">
          <h3>Which calls get reminders at all</h3>
          <ConditionsEditor
            conditions={config.entry?.conditions}
            fields={fields}
            title="Remind about a call when the company"
            emptyNote="No filter, so every call on the calendar with a known contact gets reminders."
            onChange={(next) => patch("entry", { conditions: next })}
          />
        </section>

        <p className="crm-hint crm-auto-note">
          Sequences are tried top to bottom and the first that matches wins, so order them from the
          most specific down. A call that matches none gets no reminders, which is deliberate: copy
          written for a first call is the wrong thing to send before a third.
        </p>

        {sequences.map((sequence, index) => (
          <Sequence
            key={index}
            sequence={sequence}
            index={index}
            total={sequences.length}
            fields={fields}
            variables={variables}
            threadMode={config.template?.thread}
            pick={pick}
            onPick={onPick || (() => {})}
            onChange={(next) => setSequences(sequences.map((s, j) => (j === index ? next : s)))}
            onRemove={() => setSequences(sequences.filter((_, j) => j !== index))}
            onMove={(to) => move(index, to)}
          />
        ))}

        <button
          type="button"
          className="crm-rb__add"
          onClick={() =>
            setSequences([
              ...sequences,
              { key: "", name: "", call_number: "any", conditions: null, reminders: [BLANK_REMINDER] },
            ])
          }
        >
          + add a sequence
        </button>
      </div>

      <Preview id={id} config={config} />
    </div>
  );
}

export { describeOffset, offsetMinutes };
